// HTML serialization is clipboard-only; persisted documents use Tiptap JSON.

interface ClampNumberAttributeOptions {
    max: number;
    min: number;
    integer?: boolean;
}

export const clampNumberAttribute = (
    value: unknown,
    fallback: number,
    {max, min, integer = false}: ClampNumberAttributeOptions,
): number => {
    const numberValue = typeof value === "number" ? value : Number(value);

    if (!Number.isFinite(numberValue)) return fallback;

    const normalizedValue = integer ? Math.floor(numberValue) : numberValue;

    return Math.min(max, Math.max(min, normalizedValue));
};

export const parseEnumAttribute = <T extends string>(
    value: unknown,
    allowedValues: readonly T[],
    fallback: T,
): T => (
    typeof value === "string" && (allowedValues as readonly string[]).includes(value)
        ? value as T
        : fallback
);

export const parseTextAttribute = (value: unknown, fallback: string): string => (
    typeof value === "string" ? value : fallback
);

export interface PrintoutBlockTextStyle {
    bold: boolean;
    italic: boolean;
}

export const DEFAULT_PRINTOUT_BLOCK_TEXT_STYLE: PrintoutBlockTextStyle = {bold: false, italic: false};

export const parseTextStyleAttribute = (value: unknown): PrintoutBlockTextStyle => {
    if (value && typeof value === "object") {
        const style = value as Partial<PrintoutBlockTextStyle>;

        return {bold: style.bold === true, italic: style.italic === true};
    }

    return DEFAULT_PRINTOUT_BLOCK_TEXT_STYLE;
};

export const parseBooleanAttribute = (value: unknown, fallback: boolean): boolean => {
    if (typeof value === "boolean") return value;
    if (value === "true") return true;
    if (value === "false") return false;

    return fallback;
};

export const parseColorAttribute = (value: unknown): string | null => {
    if (typeof value !== "string") return null;

    const trimmedValue = value.trim();

    return trimmedValue ? trimmedValue : null;
};

export const parseJsonAttribute = <T>(value: string | null, label: string): T | null => {
    if (!value) return null;

    try {
        const parsed = JSON.parse(value);

        if (parsed && typeof parsed === "object") {
            return parsed as T;
        }
    } catch (error) {
        console.warn(`Failed to parse ${label}`, error);
    }

    return null;
};
